/**
 * The deputy app's top bar — the same on every screen (Serve Mode, phone view, Schedule, Stats…):
 * ☰ menu on the left; UNAVAILABLE / ON DUTY / OFF DUTY / LOCK on the right.
 */
import type React from 'react';
import { isBlueHQ, productName, screenInProduct } from '../shared/site.ts';
import { BlueMark } from '../shared/BlueMark.tsx';
import { useEffect, useState } from 'react';
import { chooseSerialGps, disconnectSerialGps, onSerialGpsState, serialGpsState, serialSupported } from './serialgps.ts';
import { useAuth } from '../shared/auth.tsx';
import { useRouter } from '../shared/router.tsx';
import { useAction } from '../shared/ui.tsx';
import { askNotificationPermission, useDuty } from './duty.tsx';
import { useTheme } from '../shared/theme.ts';
import { Icon } from '../shared/icons.tsx';
import { useMailCount } from '../ops/common.tsx';

export type MenuItem = { path: string; label: string; icon: string; hint?: string; perm?: string[] };
type Section = { title: string | null; items: MenuItem[] };

/**
 * The deputy menu, in sections (side column on laptops, ☰ on phones). Items outside the agency's product
 * (BlueHQ vs CivilHound) and without the needed permission are left out. Civil papers: deputies who serve
 * them get the serve screens; patrol gets a look-up of active papers and the evictions list.
 */
export function menuSections(can: (p: string) => boolean, servesCivil: boolean): Section[] {
  const civil: MenuItem[] = [
    ...(servesCivil
      ? [
          { path: '/field', label: 'Serve papers', icon: 'car', hint: 'Your papers and route' },
          { path: '/field/map', label: 'Map', icon: 'map', hint: 'Your papers on the map' },
          { path: '/field/scan', label: 'Scan a paper', icon: 'barcode', hint: 'Scan a paper’s barcode' },
          { path: '/field/transfers', label: 'Transfers', icon: 'transfer', hint: 'Papers from and to other agencies' },
        ]
      : []),
    { path: '/field/civil-search', label: 'Active civil papers', icon: 'search', hint: 'Does this person or address have papers waiting?' },
    { path: '/field/evictions', label: 'Evictions', icon: 'door', hint: 'Recent and upcoming evictions' },
  ];
  const sections: Section[] = [
    { title: null, items: [
      { path: '/field/home', label: 'Home', icon: 'attention', hint: 'What needs your attention' },
      { path: '/field/messages', label: 'Messages', icon: 'mail', hint: 'Messages from your supervisor and the office' },
    ] },
    { title: 'Records', items: [
      { path: '/field/reports', label: 'Incident reports', icon: 'report', hint: 'Write incident reports and see where they stand' },
      { path: '/field/tickets', label: 'E-tickets', icon: 'pen', hint: 'Citations and written warnings', perm: ['reports.write', 'reports.read'] },
      { path: '/field/evidence', label: 'Evidence', icon: 'evidence', hint: 'Evidence you collected and the evidence room' },
      { path: '/field/uof', label: 'Use of force', icon: 'attention', hint: 'Response to resistance reports', perm: ['reports.write', 'reports.approve', 'ews.review'] },
      { path: '/field/tows', label: 'Tow reports', icon: 'tag', hint: 'Vehicles you had towed or impounded', perm: ['reports.write', 'reports.read'] },
      { path: '/field/forms', label: 'Forms', icon: 'clipboard', hint: 'Rights form, custodial promise, NCIC request — signed on your screen, printed like paper', perm: ['reports.write', 'reports.read', 'dispatch.view', 'warrants.enter'] },
      { path: '/field/people', label: 'People & vehicles', icon: 'people', hint: 'Look up a name, tag or VIN across reports' },
      { path: '/field/photos', label: 'Upload photos', icon: 'upload', hint: 'Add photos to a case and label what they show' },
      { path: '/field/intel', label: 'Field intel', icon: 'search', hint: 'Log what you saw; search what others saw', perm: ['reports.write', 'reports.read', 'reports.approve', 'cases.investigate'] },
      { path: '/field/cases', label: 'My cases', icon: 'route', hint: 'Follow your cases: investigations, solicitor, court, outcome' },
      { path: '/field/narcan', label: 'Narcan', icon: 'attention', hint: 'Narcan used on calls; supervisors verify the form', perm: ['reports.write', 'reports.approve'] },
      { path: '/field/cases/activity', label: 'Case activity', icon: 'chart', hint: 'Every case and its last action', perm: ['reports.approve', 'cases.investigate'] },
    ] },
    { title: 'Schools', items: [
      { path: '/field/schools/details', label: 'After-school details', icon: 'calendar', hint: 'Off-duty work at school events: sign up, and who’s working what' },
      { path: '/field/schools/desk', label: 'Desk reports', icon: 'clipboard', hint: 'Information only — never for a charge', perm: ['schools.manage', 'reports.approve'] },
      { path: '/field/schools/safety', label: 'Safety concerns', icon: 'attention', hint: 'Problems at a school: open → reported → fixed', perm: ['schools.manage', 'reports.approve'] },
      { path: '/field/schools', label: 'Schools & lockdown drills', icon: 'book', hint: 'Your schools; run a lockdown drill room by room', perm: ['schools.manage', 'reports.write'] },
    ] },
    { title: 'Arrests', items: [
      { path: '/field/arrests', label: 'Recent arrests', icon: 'arrest', hint: 'From the jail system', perm: ['reports.write', 'reports.read', 'reports.approve', 'dispatch.view', 'warrants.view'] },
    ] },
    { title: 'Details', items: [
      { path: '/field/events', label: 'Events', icon: 'calendar', hint: 'Sign up: there, standby or not' },
      { path: '/field/bolos', label: 'BOLOs', icon: 'bolo', hint: 'Be on the lookout — vehicles and people', perm: ['reports.write', 'reports.approve', 'dispatch.view'] },
      { path: '/field/patrol-map', label: 'Field map', icon: 'map', hint: 'The map with layers: deputies on duty, command boards, zones' },
      { path: '/field/command', label: 'Command boards', icon: 'attention', hint: 'Bush bond, perimeter, search: who’s responding' },
      { path: '/field/resources', label: 'Resources on duty', icon: 'people', hint: 'Who on duty is BA, SFST, drone, EMT, shield…' },
      { path: '/field/house-checks', label: 'House checks', icon: 'door', hint: 'Homes to check while residents are away', perm: ['reports.write', 'reports.approve', 'dispatch.view'] },
      { path: '/field/escorts', label: 'Funeral escorts', icon: 'calendar', hint: 'Funeral escort requests and who’s working them', perm: ['reports.write', 'reports.approve', 'dispatch.view'] },
    ] },
    { title: 'Warrants', items: [
      { path: '/field/warrants', label: 'Active warrants', icon: 'search', hint: 'Search the active warrant file', perm: ['warrants.view', 'warrants.enter'] },
      { path: '/field/warrants/request', label: 'Request a warrant', icon: 'plus', hint: 'Arrest or search warrant', perm: ['warrants.request'] },
      { path: '/field/warrants/mine', label: 'My warrants', icon: 'route', hint: 'Follow the warrants you asked for', perm: ['warrants.request'] },
      { path: '/field/warrants/queue', label: 'Requests', icon: 'clipboard', hint: 'Warrant requests to process or sign', perm: ['warrants.enter', 'warrants.sign'] },
      { path: '/field/warrants/entry', label: 'Warrant entry', icon: 'intake', hint: 'Enter a warrant by hand', perm: ['warrants.enter'] },
    ] },
    { title: 'Civil papers', items: civil },
    { title: 'Court', items: [
      { path: '/field/court', label: 'Court schedule', icon: 'gavel', hint: 'The court calendar' },
      { path: '/field/court/upcoming', label: 'Upcoming cases', icon: 'calendar', hint: 'Your court dates: security and testifying' },
      { path: '/field/on-call', label: 'On-call magistrate', icon: 'phone', hint: 'Who to call for warrants and bond' },
      { path: '/field/case-files', label: 'Case files', icon: 'folder', hint: 'Case files you owe and the ones sent to the solicitor', perm: ['casefiles.share', 'casefiles.view'] },
    ] },
    { title: 'Critical notices & PIO', items: [
      { path: '/field/pio/notices/new', label: 'Critical incident notice', icon: 'attention', hint: 'Something newsworthy? Tell the sheriff and command staff now' },
      { path: '/field/pio/notices', label: 'Critical notices', icon: 'clipboard', hint: 'Notices you sent or got' },
      { path: '/field/tips', label: 'Tips', icon: 'search', hint: 'Anonymous tips from the public', perm: ['tips.manage'] },
      { path: '/field/pio/foia', label: 'FOIA requests', icon: 'folder', hint: 'The seven steps; whose turn it is', perm: ['pio.write', 'pio.approve', 'admin.config'] },
      { path: '/field/pio', label: 'Releases & statements', icon: 'pen', hint: 'Media releases and case statements', perm: ['pio.write', 'pio.approve'] },
    ] },
    { title: 'Personnel', items: [
      { path: '/field/stats', label: 'My activity', icon: 'chart', hint: 'Your numbers: reports, tickets, arrests, tows, miles, papers served' },
      { path: '/field/daily-log', label: 'Daily activity log', icon: 'clipboard', hint: 'Your shift log for your supervisor', perm: ['reports.write'] },
      { path: '/field/daily-logs', label: 'Activity logs to review', icon: 'clipboard', hint: 'Deputies’ daily logs to approve', perm: ['reports.approve'] },
      { path: '/field/personnel/me', label: 'My record', icon: 'user', hint: 'Commendations and write-ups issued to you' },
      { path: '/field/hr/me', label: 'My pay', icon: 'chart', hint: 'Your pay, how it’s figured, and your pay history' },
      { path: '/field/evaluations', label: 'Evaluations', icon: 'clipboard', hint: 'Evaluations you write', perm: ['reports.approve', 'hr.manage'] },
      { path: '/field/hr/applicants', label: 'Applicants', icon: 'user', hint: 'Employment applications and the New Hire Checklist', perm: ['hr.manage', 'personnel.manage', 'dispatch.view'] },
      { path: '/field/bwc', label: 'Body cam reviews', icon: 'monitor', hint: 'Supervisor reviews and the monthly report', perm: ['reports.approve', 'ews.review', 'personnel.manage'] },
      { path: '/field/shutoffs', label: 'Shut-offs', icon: 'lock', hint: 'Access cards, RFID, insurance, fuel cards to confirm', perm: ['admin.config', 'hr.manage', 'fleet.manage'] },
      { path: '/field/dues', label: 'No-Shave dues', icon: 'chart', hint: 'Who’s in and who has paid this month', perm: ['dues.manage'] },
      { path: '/field/hr', label: 'Employees & pay', icon: 'people', hint: 'HR', perm: ['hr.manage'] },
      { path: '/field/personnel', label: 'Write-ups & commendations', icon: 'clipboard', hint: 'Personnel actions you write', perm: ['reports.approve', 'personnel.manage', 'ews.review'] },
      { path: '/field/ia', label: 'Internal affairs', icon: 'lock', hint: 'IA cases (IA and assigned investigators)', perm: ['ia.manage'] },
      { path: '/field/ews', label: 'Early warning', icon: 'attention', hint: 'Officers over a threshold (command staff)', perm: ['ews.review'] },
    ] },
    { title: 'Training', items: [
      { path: '/field/training', label: 'My training', icon: 'book', hint: 'Your certification, recert progress, classes, certificates' },
      { path: '/field/training/officers', label: 'Training officer', icon: 'people', hint: 'Everyone’s training status; enter training', perm: ['training.manage', 'training.view'] },
      { path: '/field/training/review', label: 'Certificates to file', icon: 'clipboard', hint: 'Certificates officers uploaded', perm: ['training.manage'] },
      { path: '/field/training/classes', label: 'Training classes', icon: 'calendar', hint: 'Schedule classes and take attendance', perm: ['training.manage'] },
    ] },
    { title: 'Equipment', items: [
      { path: '/field/mileage', label: 'My mileage', icon: 'car', hint: 'Enter your car’s mileage (weekly; daily near an oil change)' },
      { path: '/field/inspections', label: 'Vehicle inspection', icon: 'car', hint: 'Inspect your car (every 30 days)' },
      { path: '/field/my-gear', label: 'My equipment', icon: 'clipboard', hint: 'Your car and equipment; certify your inventory' },
      { path: '/field/equipment/request', label: 'Request equipment', icon: 'plus', hint: 'Ask the quartermaster for something' },
      { path: '/field/equipment/report', label: 'Equipment report', icon: 'clipboard', hint: 'Every radio, camera, car… on one page', perm: ['fleet.manage', 'schedule.manage', 'reports.approve'] },
      { path: '/field/spares', label: 'Spare cars', icon: 'car', hint: 'Car in the shop? Take a spare' },
    ] },
    { title: 'Resources', items: [
      { path: '/field/policies', label: 'Policies', icon: 'book', hint: 'Search policies; read and sign the ones assigned to you' },
      { path: '/field/call-numbers', label: 'Radio call numbers', icon: 'radio', hint: 'Who has which call number, and who had it on a date' },
      { path: '/field/directory', label: 'Directory', icon: 'directory', hint: 'Phone, call number, shift, supervisor, car' },
      { path: '/field/alerts', label: 'Alerts', icon: 'bell', hint: 'Who gets which alert, and how', perm: ['admin.config', 'admin.users', 'pio.approve'] },
      { path: '/field/schedule', label: 'Schedule', icon: 'calendar', hint: 'Your calendar and where deputies are needed' },
      { path: '/field/phones', label: 'Important phone numbers', icon: 'phone', hint: 'Numbers the office keeps for you' },
      { path: '/field/resources', label: 'Forms & office', icon: 'folder', hint: 'Request forms and the office’s contact' },
    ] },
    { title: 'More', items: [
      { path: '/field/permits', label: 'Scrap permit lookup', icon: 'recycle', hint: 'Look up a nonferrous metal permit' },
      { path: '/field/signature', label: 'My signature', icon: 'pen', hint: 'Saved signature for affidavits' },
      { path: '/field/security', label: 'Fingerprint sign-in', icon: 'fingerprint', hint: 'Sign in and unlock with your fingerprint' },
    ] },
  ];
  return sections
    .map((sec) => ({ ...sec, items: sec.items.filter((m) => screenInProduct(m.path) && (!m.perm || m.perm.some((x) => can(x)))) }))
    .filter((sec) => sec.items.length);
}

/** Every screen's title (for the top bar). */
const ALL: MenuItem[] = menuSections(() => true, true).flatMap((s) => s.items);
/** Kept for screens that look items up by path. */
export const MENU = ALL;

export function titleFor(path: string, serveMode: boolean) {
  if (path === '/field') return serveMode ? 'SERVE MODE' : 'Serve';
  const m = [...ALL].sort((a, b) => b.path.length - a.path.length).find((x) => path.startsWith(x.path) && x.path !== '/field');
  if (m) return m.label;
  if (path.startsWith('/field/paper') || path.startsWith('/field/arrive')) return 'Serve';
  return productName();
}

/** UNAVAILABLE / ON DUTY / OFF DUTY — the same three keys everywhere. */
function DutyKeys() {
  const { duty, call } = useDuty();
  const a = useAction();
  const status = duty?.status ?? 'off';
  return (
    <>
      <button
        className={`fh-key ${status === 'unavailable' ? 'on unavailable' : ''}`}
        disabled={!duty?.onDuty || a.busy}
        title={status === 'unavailable' && duty?.unavailableReason ? `Unavailable: ${duty.unavailableReason} — tap to be available` : 'Lunch, court, vehicle…'}
        onClick={() => {
          if (status === 'unavailable') return a.run(() => call('/api/field/status', { status: 'available' }));
          const reason = window.prompt('Unavailable because… (lunch, court, vehicle…)')?.trim();
          if (reason) a.run(() => call('/api/field/status', { status: 'unavailable', reason }));
        }}
      >
        <span className="long">UNAVAILABLE</span>
        <span className="short">UNAVAIL</span>
      </button>
      <button
        className={`fh-key ${duty?.onDuty ? 'on duty' : ''}`}
        disabled={!!duty?.onDuty || a.busy || !duty}
        onClick={() => (askNotificationPermission(), a.run(() => call('/api/field/duty', { onDuty: true })))}
      >
        ON DUTY
      </button>
      <button
        className={`fh-key ${duty && !duty.onDuty ? 'on offduty' : ''}`}
        disabled={!duty?.onDuty || a.busy}
        onClick={() => window.confirm('Go off duty? Location sharing stops.') && a.run(() => call('/api/field/duty', { onDuty: false }))}
      >
        OFF DUTY
      </button>
    </>
  );
}

export function FieldHeader({
  title,
  online,
  syncing,
  syncNote,
  pending,
  onSync,
  onMenu,
  onLock,
  showDuty = true,
  sideOpen,
}: {
  title: string;
  online: boolean;
  syncing: boolean;
  syncNote?: string | null;
  pending?: number;
  onSync: () => void;
  onMenu: () => void;
  onLock: () => void;
  /** UNAVAILABLE / ON DUTY / OFF DUTY only on the civil-serve screens. */
  showDuty?: boolean;
  /** The side menu is showing (the ☰ button hides it). */
  sideOpen?: boolean;
}) {
  const mail = useMailCount(online && isBlueHQ());
  return (
    <header className="fh">
      <div className="fh-left">
        <button
          className="fh-burger"
          onClick={onMenu}
          aria-label={sideOpen !== undefined ? (sideOpen ? 'Hide menu' : 'Show menu') : mail?.unread ? `Menu — ${mail.unread} unread message${mail.unread === 1 ? '' : 's'}` : 'Menu'}
          aria-expanded={sideOpen}
          aria-haspopup={sideOpen === undefined ? 'dialog' : undefined}
        >
          <span />
          <span />
          <span />
          {mail?.unread ? <i className={`fh-dot ${mail.overdue ? 'bad' : ''}`} aria-hidden /> : null}
        </button>
        {isBlueHQ() && <BlueMark size={30} className="fh-mark" />}
        <span className="fh-title">{title}</span>
        <span className={`net ${online ? 'on' : 'off'}`}>{online ? 'Online' : 'Offline'}</span>
        <button className="fh-sync" onClick={onSync} disabled={syncing || !online} title="Sync now" aria-label="Sync now">
          {syncing ? '…' : '⟳'}
        </button>
        <FullScreenButton />
        {!!pending && <span className="fh-note">{pending} queued</span>}
        {syncNote && <span className="fh-note hide-narrow">{syncNote}</span>}
      </div>
      <div className="fh-keys" role="group" aria-label="Duty status">
        {showDuty && <DutyKeys />}
        <button className="fh-key lock" onClick={onLock} aria-label="Lock screen" title="Lock the screen">
          <Icon name="lock" size={15} /> <span className="long">LOCK</span>
        </button>
      </div>
    </header>
  );
}

/** The menu as a column on the left (laptops and tablets), until the deputy hides it with ☰. */
export function FieldSide({ onSignOut }: { onSignOut: () => void }) {
  const { path, navigate } = useRouter();
  const { can, me } = useAuth();
  const servesCivil = me?.servesCivil ?? !isBlueHQ();
  const mail = useMailCount(isBlueHQ());
  const active = (p: string) =>
    p === '/field' ? path === '/field' || path.startsWith('/field/paper') || path.startsWith('/field/arrive')
    : p === '/field/warrants' ? path === p || /^\/field\/warrants\/(?!request$|mine$|queue$|entry$)/.test(path.split('?')[0]!)
    : p === '/field/court' ? path === p || /^\/field\/court\/(?!upcoming$)/.test(path.split('?')[0]!)
    : path.split('?')[0]!.startsWith(p);
  const item = (m: { path: string; label: string; icon?: string }) => (
    <button key={m.path} type="button" className={`${active(m.path) ? 'active' : ''} ${m.path === '/field/messages' ? 'fs-mail' : ''}`} onClick={() => navigate(m.path)} aria-current={active(m.path) ? 'page' : undefined}>
      {m.icon ? <Icon name={m.icon} size={m.path === '/field/messages' ? 22 : 18} /> : <span className="fs-dot" aria-hidden />}
      <span>{m.label}</span>
      {m.path === '/field/messages' && mail?.unread ? <span className={`mail-n inline ${mail.overdue ? 'bad' : ''}`}>{mail.unread}</span> : null}
    </button>
  );
  return (
    <nav className="field-side" aria-label="Menu">
      {menuSections(can, servesCivil).map((sec) => (
        <div key={sec.title ?? 'top'} style={{ display: 'contents' }}>
          {sec.title && <div className="fs-h">{sec.title}</div>}
          {sec.items.map(item)}
        </div>
      ))}
      <button type="button" className="fs-out" onClick={onSignOut}>
        <Icon name="logout" size={18} />
        <span>Sign out</span>
      </button>
    </nav>
  );
}

/** Slide-out menu from the ☰ button. */
export function FieldMenu({
  open,
  onClose,
  wide,
  serveMode,
  onToggleView,
  onSignOut,
  onCustomize,
}: {
  open: boolean;
  onClose: () => void;
  wide: boolean;
  serveMode: boolean;
  onToggleView: () => void;
  onSignOut: () => void;
  onCustomize?: () => void;
}) {
  const { me, can } = useAuth();
  const { path, navigate } = useRouter();
  const mail = useMailCount(open && isBlueHQ());
  useEffect(() => {
    if (!open) return;
    const k = (e: KeyboardEvent) => e.key === 'Escape' && onClose();
    window.addEventListener('keydown', k);
    return () => window.removeEventListener('keydown', k);
  }, [open, onClose]);
  if (!open) return null;
  const go = (p: string) => (navigate(p), onClose());
  const active = (p: string) => (p === '/field' ? path === '/field' || path.startsWith('/field/paper') : path.startsWith(p));
  return (
    <div className="fm-back" onClick={onClose}>
      <nav className="fm" role="dialog" aria-label="Menu" onClick={(e: React.MouseEvent) => e.stopPropagation()}>
        <div className="fm-h">
          {isBlueHQ() ? <BlueMark size={34} /> : <img src="/civilhound-icon-clear.webp" alt="" width={34} height={34} />}
          <div>
            <strong>{me?.user.displayName}</strong>
            <div className="small">{me?.agency.name}</div>
          </div>
          <button className="fm-x" onClick={onClose} aria-label="Close menu">
            ✕
          </button>
        </div>
        {menuSections(can, me?.servesCivil ?? !isBlueHQ()).map((sec) => (
          <div key={sec.title ?? 'top'}>
            {sec.title && <div className="fm-h2">{sec.title}</div>}
            <ul className="fm-main">
              {sec.items.map((m) => (
                <li key={m.path}>
                  <button className={`${active(m.path) ? 'active' : ''} ${m.path === '/field/messages' ? 'fm-mail' : ''}`} onClick={() => go(m.path)}>
                    <span className="fm-i" aria-hidden>
                      <Icon name={m.icon} size={m.path === '/field/messages' ? 24 : 20} />
                    </span>
                    <span>
                      <strong>
                        {m.label}
                        {m.path === '/field/messages' && mail?.unread ? <span className="mail-n inline">{mail.unread}</span> : null}
                      </strong>
                      <small>{m.path === '/field/messages' && mail?.overdue ? `${mail.overdue} reply overdue` : m.hint}</small>
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          </div>
        ))}
        <SerialGpsControl />
        <ul className="fm-more">
          {onCustomize && (
            <li>
              <button onClick={onCustomize}>⚙ Customize screen</button>
            </li>
          )}
          {wide && (
            <li>
              <button onClick={() => (onToggleView(), onClose())}>{serveMode ? 'Switch to phone view' : 'Switch to Serve Mode (laptop)'}</button>
            </li>
          )}
          {me?.user.role !== 'process_server' && (
            <li>
              <button onClick={() => go('/')}>Office</button>
            </li>
          )}
        </ul>
        <ThemePicker />
        <button className="fm-out" onClick={onSignOut}>
          Sign out
        </button>
      </nav>
    </div>
  );
}

/** Full screen: hides the browser's tabs, address bar and the Windows taskbar. Esc (or the button) exits. */
export function FullScreenButton() {
  const [on, setOn] = useState(() => !!document.fullscreenElement);
  useEffect(() => {
    const f = () => setOn(!!document.fullscreenElement);
    document.addEventListener('fullscreenchange', f);
    return () => document.removeEventListener('fullscreenchange', f);
  }, []);
  // iPhones don't allow it from a web page; there, "Add to Home Screen" opens CivilHound without the browser bars.
  if (!document.fullscreenEnabled) return null;
  const toggle = () => (document.fullscreenElement ? document.exitFullscreen() : document.documentElement.requestFullscreen({ navigationUI: 'hide' })).catch(() => undefined);
  return (
    <button className="fh-sync fh-full" onClick={toggle} aria-label={on ? 'Exit full screen' : 'Full screen'} title={on ? 'Exit full screen (Esc)' : 'Full screen'}>
      {on ? '⤡' : '⛶'}
    </button>
  );
}

/** ☰ menu: connect a USB GPS (or the laptop's built-in GPS port) when Windows location is slow. */
export function SerialGpsControl() {
  const [s, setS] = useState(serialGpsState);
  useEffect(() => onSerialGpsState(setS), []);
  if (!serialSupported) return null;
  const label =
    s.status === 'on' ? `GPS connected · ${s.satellites ?? '?'} satellites` :
    s.status === 'nofix' ? 'GPS connected · waiting for satellites…' :
    s.status === 'connecting' ? 'Connecting to GPS…' :
    s.remembered ? 'GPS not connected — plug it in' : 'Use a USB / built-in GPS';
  return (
    <div className={`fm-gps ${s.status}`}>
      <div className="fm-gps-l">
        <span aria-hidden>📡</span>
        <span>
          <strong>{label}</strong>
          <small>
            {s.status === 'on' || s.status === 'nofix'
              ? `Updates every second${s.baud ? ` · ${s.baud} baud` : ''}. Used instead of Windows location.`
              : s.error ?? 'Faster, more exact location than Windows gives Chrome. Pick the GPS’s COM port once.'}
          </small>
        </span>
      </div>
      <div className="row gap-sm">
        <button className="btn sm primary" disabled={s.status === 'connecting'} onClick={() => void chooseSerialGps()}>
          {s.status === 'on' || s.status === 'nofix' ? 'Change port' : 'Connect GPS'}
        </button>
        {(s.remembered || s.status === 'on' || s.status === 'nofix') && (
          <button className="btn sm ghost" onClick={() => void disconnectSerialGps()}>
            Disconnect
          </button>
        )}
      </div>
    </div>
  );
}

/** Light / dark / match the phone — dark is easier on the eyes in a patrol car at night. */
function ThemePicker() {
  const [theme, setTheme] = useTheme();
  return (
    <div className="fm-theme">
      <span className="small muted">Theme</span>
      <div className="seg-ctl" role="radiogroup" aria-label="Theme">
        {(
          [
            ['light', 'Light'],
            ['dark', 'Dark'],
            ['auto', 'Phone'],
          ] as const
        ).map(([k, l]) => (
          <button key={k} type="button" role="radio" aria-checked={theme === k} aria-pressed={theme === k} className={theme === k ? 'on' : ''} onClick={() => setTheme(k)}>
            {l}
          </button>
        ))}
      </div>
    </div>
  );
}
